import { Form } from '@app/components/various/Form';
import { addToast } from '@app/components/various/Toasts';
import { fetchConfigurationRequest, getBackgroundImage, getDarkLogoImage, getIsStockTypeSeparationEnabled } from '@app/ducks';
import { getImageFromProps } from '@app/ducks/helpers';
import { paths } from '@app/paths';
import { useEditCustomisationSettings } from '@app/services/hooks/settings/useEditCustomisationSettings';
import { useSettings } from '@app/services/hooks/settings/useSettings';
import { Settings } from '@typings';
import { isEmpty } from 'ramda';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { SettingsLayout } from '../SettingsLayout';

import { AllProductsLayoutFields } from './CustomisationSection/AllProductsLayoutFields';
import { BrandElementsFields } from './CustomisationSection/BrandElementsFields';
import { CustomTextsFields } from './CustomisationSection/CustomTextsFields';

export const CustomizationSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'settings']);
  const defaultBackgroundImage = useSelector(getBackgroundImage);
  const defaultLogoImage = useSelector(getDarkLogoImage);
  const isStockSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const {
    data: { allProductsViewLayout, companyName, loginPageMessage, soldOutLabel, stockTypeSeparation } = {},
    isPending: isLoadingSettings,
  } = useSettings();
  const { mutate: editCustomisationSettings, isPending: isSavingChanges } = useEditCustomisationSettings();

  const defaultValues = React.useMemo(
    () => ({
      allProductsViewLayout,
      backgroundImage: getImageFromProps(defaultBackgroundImage, 'backgroundImage'),
      companyName,
      loginPageMessage,
      logoImage: getImageFromProps(defaultLogoImage, 'logoImageDark'),
      soldOutLabel,
      ...(isStockSeparationEnabled && {
        preorderStockTypeLabel: stockTypeSeparation?.stockTypeLabels.preorder,
        stockTypeLabel: stockTypeSeparation?.stockTypeLabels.stock,
      }),
    }),
    [
      allProductsViewLayout,
      companyName,
      defaultBackgroundImage,
      defaultLogoImage,
      isStockSeparationEnabled,
      loginPageMessage,
      soldOutLabel,
      stockTypeSeparation,
    ],
  );

  const formMethods = useForm<Settings.CustomisationFormFields>({
    defaultValues,
  });

  React.useEffect(() => {
    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods]);

  const handleSubmit = async (values: Settings.CustomisationFormFields) => {
    editCustomisationSettings([defaultValues, values], {
      onError: () => {
        addToast(t('settings:customisation_update_fail'), { type: 'error' });
      },
      onSuccess: ({ errors }) => {
        dispatch(fetchConfigurationRequest({ ignoreCache: true }));

        if (!isEmpty(errors)) {
          return errors.forEach(message => addToast(message, { type: 'error' }));
        }

        addToast(t('settings:customisation_updated'), { type: 'success' });
      },
    });
  };

  const handleCancel = () => {
    formMethods.reset(defaultValues);
  };
  const formName = 'CustomizationForm';

  return (
    <SettingsLayout
      activePath={paths.SETTINGS_CUSTOMIZATION}
      isLoading={isSavingChanges}
      hasChanges={formMethods.formState.isDirty}
      isDisabled={isSavingChanges || isLoadingSettings}
      onCancel={handleCancel}
      formName={formName}
    >
      <Form formMethods={formMethods} onSubmit={handleSubmit} id={formName}>
        <div className="space-y-4">
          <BrandElementsFields isDisabled={isSavingChanges} isLoading={isLoadingSettings} />
          <CustomTextsFields isDisabled={isSavingChanges} isLoading={isLoadingSettings} />
          <AllProductsLayoutFields isDisabled={isSavingChanges} isLoading={isLoadingSettings} />
        </div>
      </Form>
    </SettingsLayout>
  );
};
